import SwitchBar from '../../../components/switch-bar';
import Header from '../../../components/common/global-top-bar';
import { TabOptions } from '../clients-constants';

import React, { useEffect, useRef, useMemo } from 'react';
import RichTextEditor from '../../../components/rich-text-editor/rich-text-editor';
import { useForm } from 'react-hook-form';
import {
  useDefaultSettings,
  useSettings,
  useUpdateSettings,
  useUpdateSystemSettings,
} from '../../../hooks/useSettings';
import { toast } from 'react-toastify';

interface DefaultLandingPageContent {
  landing_page_message: string;
  accept_policy_description: string;
}
const DefaultThemeConfiguration: React.FC = () => {
  const { data: appSettings } = useSettings('email');
  const { data: defaultSettings } = useDefaultSettings();
  const updateSettings = useUpdateSettings();

  const defaultThemeConfig = useMemo(() => {
    if (!defaultSettings) return null;

    return {
      landing_page_message: defaultSettings.landing_page_message || '',
      accept_policy_description: defaultSettings.accept_policy_description || '',
    };
  }, [defaultSettings]);

  const {
    setValue,
    handleSubmit,
    formState: { isDirty },
    watch,
    reset,
  } = useForm<DefaultLandingPageContent>({
    defaultValues: defaultThemeConfig || {
      landing_page_message: '',
      accept_policy_description: '',
    },
  });

  useEffect(() => {
    if (defaultThemeConfig) {
      reset(defaultThemeConfig);
    }
  }, [defaultThemeConfig, reset]);

  const onSubmit = (data: DefaultLandingPageContent) => {
    const settingsPromise = updateSettings.mutateAsync({
      data: {
        message: appSettings?.message,
        system: {
          ...appSettings?.system,
          LANDING_PAGE_MESSAGE: data.landing_page_message,
          ACCEPT_POLICY_DESCRIPTION: data.accept_policy_description,
        },
      },
    });

    Promise.all([settingsPromise])
      .then(() => {
        toast.success('Settings updated successfully');
      })
      .catch((error) => {
        console.error('Error updating settings:', error);
        toast.error('Failed to update settings');
      });
  };

  const formValues = watch();

  return (
    <div key="default-settings-container">
      <Header heading={'Default Content'} />
      <SwitchBar heading={TabOptions} position={2} />
      {/* <SwitchBar heading={HeaderOptions} position={2} /> */}
      <div className="space-y-6 p-6">
        <p className="mb-2 text-sm text-gray-500">
          These configuration options set the default content for our MWA 4.0 landing page for all clients who have
          configured their own content.
        </p>
        <div className="flex items-center justify-between">
          <div className="text-2xl font-bold">Default Landing Page Content</div>
          <button
            type="submit"
            form="theme-form"
            disabled={!isDirty}
            className={`rounded px-6 py-2 ${isDirty ? 'btn-darkblue text-white' : 'cursor-not-allowed bg-gray-300'}`}
          >
            Save Changes
          </button>
        </div>

        <form id="theme-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <div className="space-y-6">
            <div className="space-y-6">
              <div>
                <label className="mb-2 block">Landing Page Message</label>
                <p className="mb-2 text-sm text-gray-500">
                  This is the message that will be displayed in the top section of the landing page..
                </p>
                <RichTextEditor
                  content={formValues.landing_page_message}
                  onChange={(value) => setValue('landing_page_message', value, { shouldDirty: true })}
                />
              </div>
            </div>
            <div className="flex-1">
              <label className="mb-2 block">Accept Policies Description</label>
              <p className="mb-2 text-sm text-gray-500">
                This is the message that will be displayed on the T&C, Privacy Policy, and Marketing Opt-In page
                (mwa.com/accept-policies) for Trusted SSO users who have not accepted the current policies.
              </p>
              <RichTextEditor
                content={formValues.accept_policy_description}
                onChange={(value) => setValue('accept_policy_description', value, { shouldDirty: true })}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DefaultThemeConfiguration;
